import { gql } from '@apollo/client';

/**
 * Единый карьерный маршрут (ТЗ 0069).
 *
 * Имена операций намеренно отличаются от имён полей бэкенда
 * (`CareerJourneyBoard` vs `myCareerJourney`): совпадение схлопывает
 * результирующий тип кодогена в схемный. Плюс второе правило, менее
 * очевидное: `refetchQueries: ['Имя']` сопоставляется именно с ИМЕНЕМ
 * ОПЕРАЦИИ, поэтому оно обязано быть уникальным на весь проект.
 */

/**
 * Дорожная карта одним запросом.
 *
 * 🔴 Ни одного числа клиент не считает. «7 из 18», готовность, следующее
 * действие и задачи недели приходят с сервера той же функцией, что кормит
 * экран, куда ведёт кнопка этапа. Один раз увидев расхождение схемы с
 * подстраницей, ей перестают верить целиком.
 *
 * Открыто ЛЮБОМУ студенту, не только участнику центра: закрытое приходит
 * помеченным в `locked`, а не четырёхсотчетвёртым.
 */
export const CareerJourneyBoardQuery = gql`
  query CareerJourneyBoard {
    myCareerJourney {
      isMember
      isPreview
      memberStatus
      # ── ТЗ 0070 ─────────────────────────────────────────────────────
      # 🔴 Шесть счётчиков приходят РАЗДЕЛЬНО и складывать их нельзя: они
      # несопоставимы. Именно из такой суммы получался «18 / 20», который
      # показывал 90 % готовности при трёх подтверждённых навыках из
      # двенадцати.
      phase
      alignment {
        status
        goalTrackCode
        goalTitle
        profileHeadline
        profileDirection
        suggestedHeadline
      }
      sections {
        key
        state
        isCurrent
        route
        isBlocked
        blockReason
        metrics {
          key
          value
          target
          route
          isDone
          isBlocked
        }
      }
      # ── ТЗ 0071 ─────────────────────────────────────────────────────
      # Основное действие и до двух следующих шагов — ОДНИМ проходом правил
      # на сервере. Приоритет клиент не вычисляет: подписи собирает по виду и
      # причине, а данные (урок, «6 из 7», минуты) берёт отсюда.
      hasGoal
      primaryAction {
        id
        kind
        stageKey
        route
        targetId
        reasonCode
        subjectTitle
        position
        positionTotal
        expectedOutcome
        estimatedMinutes
        dueDate
      }
      secondaryActions {
        id
        kind
        stageKey
        route
        targetId
        reasonCode
        subjectTitle
        position
        positionTotal
        expectedOutcome
        estimatedMinutes
        dueDate
      }
      goal {
        trackCode
        title
        icon
        isChosen
        targetSkillsMine
        targetSkillsTotal
        # ТЗ 0071: пять чисел прогресса, те же, что в каталоге, паспорте и на
        # карте. Пусто (null), пока цель не выбрана — персонального «10 из 22» нет.
        skillProgress {
          requiredCount
          meetsRequiredLevelCount
          needsImprovementCount
          missingCount
          verifiedMeetsRequiredLevelCount
        }
        salaryFrom
        salaryTo
        salaryCurrency
      }
      stages {
        key
        done
        total
        isDone
        isBlocked
        blockReason
        route
        targetId
      }
      nextAction {
        kind
        stageKey
        route
        targetId
      }
      locked {
        key
        reasonCode
      }
      # Чек-лист резюме: те же семь проверок, из которых складывается этап
      # «CV». Экран резюме рисует их галочками — считать их там заново значило
      # бы завести второе определение полноты профиля.
      cvChecklist {
        key
        isDone
      }
      applications {
        total
        active
        applied
        interview
        offer
        rejected
        hired
        nextFollowUp {
          id
          companyName
          position
          status
          nextActionAt
        }
        recent {
          id
          companyName
          position
          status
          appliedAt
          nextActionAt
        }
      }
    }
  }
`;

/** Собственный журнал поиска работы. Пусто у не-участника и в предпросмотре. */
export const CareerApplicationsListQuery = gql`
  query CareerApplicationsList($status: String, $limit: Int) {
    myCareerApplications(status: $status, limit: $limit) {
      id
      vacancyId
      companyName
      position
      applyUrl
      status
      appliedAt
      nextActionAt
      note
      source
      isPlacementConfirmed
      createdAt
      updatedAt
    }
  }
`;
